import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        {[1, 2, 3].map(i => <div key={i} className="h-5 w-20 bg-muted animate-pulse rounded-md" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative py-2 text-sm font-medium text-foreground transition-colors hover:text-primary">
                {item.label}
                {}
                <span className="absolute -bottom-1 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-ring transition-all duration-300 ease-out group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3].map(i => <div key={i} className="h-8 w-full bg-muted animate-pulse rounded-md" data-index={i} />)}
      </div>;
  }
  return <nav className="flex flex-col space-y-4 p-6">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-lg font-medium text-foreground hover:text-primary" onClick={onClose} data-index={i}>
          {item.label}
        </Link>)}
      <div className="pt-4">
        <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90" asChild>
          <Link to="/ContactPage" onClick={onClose}>
            Získat nabídku
          </Link>
        </Button>
      </div>
    </nav>;
}
export default function Zahlavi() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="záhlaví" className={cn("fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 ease-in-out", isScrolled ? "bg-background/80 backdrop-blur-lg shadow-sm border-b border-border/50 py-3" : "bg-background border-b border-border py-5")}>
      <div className="container mx-auto flex items-center justify-between px-4 md:px-6">
        {}
        <div className="flex-shrink-0">
          <Link to="/" className="block">
            <span className="text-xl font-fonsie tracking-tighter text-primary uppercase !font-extrabold">PRáDELNA VRŠOVICE Praha</span>
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="3" className="flex">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-4">
          <Button className="hidden md:inline-flex bg-primary text-primary-foreground hover:bg-primary/90 shadow-md transition-transform hover:scale-105" asChild>
            <Link to="/ContactPage">
              Získat nabídku
            </Link>
          </Button>

          {}
          <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-primary" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="absolute top-full left-0 w-full bg-background border-b border-border shadow-lg md:hidden animate-in slide-in-from-top-5 duration-200">
          <MenuProvider menu_id="3" className="w-full">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>}
    </header>;
}